import { PickOrderSummaryFragment } from '../../../graphql'

export type ChainRequest = { id: string, requestNumber: number | string }

/** One fulfillment order in a run, and what it came from and became. */
export type ChainLink = {
	orderId: string,
	orderNumber: number | string,
	/** The demand requests behind the order. Empty for one that came from elsewhere (e.g. Shopify). */
	requests: ChainRequest[],
	/** The transfer orders its units shipped on — one per carton. Empty until the run completes. */
	transferOrderIds: string[],
}

type ChainSource = Pick<PickOrderSummaryFragment, 'fulfillmentOrders' | 'fulfillments'>

/**
 * The fulfillment chain behind a pick order, one entry per fulfillment order:
 * the request it came from, and the transfer orders its cartons became.
 *
 * A carton can carry more than one order, so a transfer order can appear under
 * several of them.
 */
export function fulfillmentChain( pickOrder: ChainSource ): ChainLink[] {
	const transferOrdersByOrder = new Map<string, Set<string>>()
	for ( const fulfillment of pickOrder.fulfillments ) {
		const transferOrderId = fulfillment.systemTransferOrder?.id
		if ( !transferOrderId ) {
			continue
		}
		for ( const line of fulfillment.lines ) {
			const orderId = String( line.fulfillmentOrderLineItem.fulfillmentOrderId )
			const ids = transferOrdersByOrder.get( orderId ) ?? new Set<string>()
			ids.add( String( transferOrderId ) )
			transferOrdersByOrder.set( orderId, ids )
		}
	}

	return pickOrder.fulfillmentOrders.map( ( order ) => ( {
		orderId: String( order.id ),
		orderNumber: order.orderNumber,
		requests: order.demandRequests.map( ( request ) => ( { id: String( request.id ), requestNumber: request.requestNumber } ) ),
		transferOrderIds: [ ...( transferOrdersByOrder.get( String( order.id ) ) ?? [] ) ]
			.sort( ( a, b ) => Number( a ) - Number( b ) ),
	} ) )
}

export const fulfillmentOrderPath = ( orderNumber: number | string ) => `/dc-admin/fulfillment-orders/${orderNumber}`
export const demandRequestPath = ( id: string ) => `/requests/${id}`
export const transferOrderPath = ( id: string ) => `/transfer-orders/${id}`

/** The fulfillment orders created for one demand request, in the order the chain flows. */
export type RequestGroup = {
	/** Null for orders that came from no request (e.g. Shopify). */
	request: ChainRequest | null,
	orders: ChainLink[],
}

/**
 * The chain as it flows — request, then the fulfillment orders made for it,
 * then their transfer orders — grouped by request, oldest request first.
 *
 * An order drawn from more than one request sits under the first; that is
 * rare, and repeating it would repeat its transfer orders too.
 */
export function chainByRequest( chain: ChainLink[] ): RequestGroup[] {
	const groups = new Map<string, RequestGroup>()
	for ( const link of chain ) {
		const request = link.requests[ 0 ] ?? null
		const key = request?.id ?? ''
		const group = groups.get( key ) ?? { request, orders: [] }
		group.orders.push( link )
		groups.set( key, group )
	}
	return [ ...groups.values() ].sort( ( a, b ) => {
		if ( !a.request || !b.request ) {
			return Number( !a.request ) - Number( !b.request )
		}
		return Number( a.request.requestNumber ) - Number( b.request.requestNumber )
	} )
}
